
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import { usePermessiStore, permessoPerPercorso } from '../stores/permessi.store';
import { landingPath } from '../lib/landing';
import { sessioneUtilizzabile } from '../lib/sessione';
import { puoAprirePagina } from '../lib/azioni';
import Loading from './ui/Loading';
import EmptyState from './ui/EmptyState';

export function NessunaSezioneAbilitata() {
  return (
    <div className="wfm-page">
      <div className="wfm-card">
        <EmptyState
          icon={ShieldAlert}
          title="Nessuna sezione abilitata"
          hint="Il tuo profilo non dispone al momento di sezioni operative abilitate. Contatta un amministratore o accedi al tuo profilo personale in alto a destra."
        />
      </div>
    </div>
  );
}

export default function ProtectedRoute({
  children,
  allowedRoles,
  codiciVista,
  requireModule,
  requireAnyModule = false,
  authOnly = false,
}) {
  const { isAuthenticated, user } = useAuthStore();
  const { permessi, catalogo, stato: statoPermessi, carica } = usePermessiStore();
  const location = useLocation();

  const sessionOk = isAuthenticated && sessioneUtilizzabile(useAuthStore.getState());
  const needsPermessi =
    sessionOk
    && user?.ruolo !== 'SUPER_ADMIN'
    && user?.stato !== 'primo_accesso'
    && user?.stato !== 'sospeso'
    && user?.stato !== 'disattivato'
    && statoPermessi === 'iniziale';

  useEffect(() => {
    if (needsPermessi) {
      carica();
    }
  }, [needsPermessi, carica]);

  if (!sessionOk) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.stato === 'primo_accesso' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (user?.stato === 'sospeso' || user?.stato === 'disattivato') {
    return <Navigate to="/login" replace />;
  }

  // Attendiamo il caricamento di `/permessi/me` prima di valutare i permessi:
  // evita sia il rimbalzo su F5 per chi ha una vista delegata sopra il proprio
  // ruolo, sia la finestra fail-open per chi ha una revoca esplicita («Nega»).
  if (
    user?.ruolo !== 'SUPER_ADMIN'
    && location.pathname !== '/onboarding'
    && (statoPermessi === 'iniziale' || statoPermessi === 'caricamento')
  ) {
    return <Loading className="min-h-[60vh]" />;
  }

  if (authOnly) {
    return children;
  }

  const accesso = statoPermessi === 'pronto' ? { permessi, catalogo } : null;
  const redirectOrEmpty = () => {
    const dest = landingPath(user, accesso);
    if (!dest || dest === location.pathname) {
      return <NessunaSezioneAbilitata />;
    }
    return <Navigate to={dest} replace />;
  };

  if (!puoAprirePagina({
    codice: permessoPerPercorso(catalogo, location.pathname),
    codiciVista,
    permessi,
    statoPermessi,
    ruolo: user?.ruolo,
    allowedRoles,
  })) {
    return redirectOrEmpty();
  }

  if (user?.ruolo !== 'SUPER_ADMIN') {
    const mods = user?.moduli_abilitati || [];
    if (requireModule && !mods.includes(requireModule)) {
      return redirectOrEmpty();
    }
    if (requireAnyModule && mods.length === 0) {
      return redirectOrEmpty();
    }
  }

  return children;
}
